import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Copy, Download, Check, RefreshCw } from "lucide-react";

/**
 * The user's affiliate link + welcome message + QR code card.
 *
 * Extracted from AffiliatePage so it can live under Referrals ("share your
 * link" is a growth mechanic, not a payouts thing).
 */
export default function AffiliateLinkCard({
  linkData, customLink, setCustomLink, welcomeMsg, setWelcomeMsg,
  savingLink, savingMsg, linkError, setLinkError,
  saveLink, saveWelcomeMessage, copyLink, downloadQR,
  accentBg, accentText, accentBorder, cardBg,
}) {
  return (
    <section data-testid="affiliate-link-section">
      <h2 className={`text-lg font-semibold mb-4 ${accentText}`}>Your Affiliate Link</h2>
      <div className={`rounded-xl border p-4 mb-4 ${cardBg}`}>
        <p className="text-xs text-white/40 uppercase tracking-wider mb-2 font-semibold">
          Tracking Reference
        </p>
        <div className="space-y-1.5 text-xs text-white/50">
          <p>
            <span className="text-white/70 font-mono">
              https://agenteros.ai/join/{linkData?.aff_id || "XXXX"}
            </span>{" "}
            — landing page with your welcome message
          </p>
          <p>
            <span className="text-white/70 font-mono">
              https://agenteros.ai?ref={linkData?.aff_id || "XXXX"}
            </span>{" "}
            — append{" "}
            <span className="font-mono text-white/70">?ref={linkData?.aff_id || "XXXX"}</span>{" "}
            to any URL to attribute the visitor to you
          </p>
        </div>
      </div>

      <div className={`rounded-xl border p-6 ${cardBg}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">
              Custom link
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={customLink}
                onChange={(e) => { setCustomLink(e.target.value); setLinkError(""); }}
                placeholder={linkData?.default_link || `/join/${linkData?.aff_id || "XXXX"}`}
                className="bg-white/5 border-white/10 text-white font-mono text-sm"
                data-testid="affiliate-link-input"
              />
              <Button
                onClick={copyLink}
                size="sm"
                className={`${accentBg} text-black shrink-0`}
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            {linkError && (
              <p className="text-xs text-red-400 mt-1" data-testid="link-error">{linkError}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <p className="text-xs text-white/30">Aff_ID: {linkData?.aff_id}</p>
              {customLink !== linkData?.link && (
                <Button
                  onClick={saveLink}
                  disabled={savingLink}
                  size="sm"
                  variant="outline"
                  className={`text-xs h-7 ${accentBorder} ${accentText}`}
                  data-testid="save-link-btn"
                >
                  {savingLink ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3 mr-1" />
                  )}
                  Save & Update QR
                </Button>
              )}
              {linkData?.is_custom && customLink === linkData?.link && (
                <Button
                  onClick={() => { setCustomLink(""); saveLink(); }}
                  size="sm"
                  variant="ghost"
                  className="text-xs h-7 text-white/30 hover:text-white/60"
                  data-testid="reset-link-btn"
                >
                  Reset to default
                </Button>
              )}
            </div>

            <div className="mt-5">
              <Label className="text-white/60 text-xs uppercase tracking-wider">
                Welcome message
              </Label>
              <Textarea
                value={welcomeMsg}
                onChange={(e) => setWelcomeMsg(e.target.value.slice(0, 500))}
                placeholder="Add a personal welcome for people who join through your link."
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[80px]"
                data-testid="welcome-message-input"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-white/30">{welcomeMsg.length}/500</span>
                <Button
                  onClick={saveWelcomeMessage}
                  disabled={savingMsg}
                  size="sm"
                  className={`${accentBg} text-black`}
                  data-testid="save-welcome-btn"
                >
                  {savingMsg ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3 mr-1" />
                  )}
                  Save
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <Label className="text-white/60 text-xs uppercase tracking-wider self-start">
              QR Code
            </Label>
            {linkData?.qr_code && (
              <div className="mt-2 p-4 bg-white rounded-xl">
                <img
                  src={`data:image/png;base64,${linkData.qr_code}`}
                  alt="Affiliate QR"
                  className="w-48 h-48"
                  data-testid="qr-code-img"
                />
              </div>
            )}
            <Button
              onClick={downloadQR}
              variant="outline"
              size="sm"
              className={`mt-3 ${accentBorder} ${accentText} hover:bg-white/5`}
              data-testid="download-qr-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download QR (1024x1024)
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
